/*
 * Build header: pure model, no React. The status pill copy, the short provider name, the cancel/retry/priority
 * availability notes and the "Open in" label.
 */
import type { BuildPriority } from '@hangar/shared';
import type { Build } from '@/api/schemas';
import { formatDuration } from '@/lib/format';
import { isTerminal } from '../buildHelpers';
import { serviceName, statusLabel } from '../buildLabels';

export const PRIORITIES: BuildPriority[] = ['low', 'normal', 'high', 'urgent'];

/** Statuses that show the live duration inside the status pill. */
const LIVE_DURATION = new Set<Build['status']>(['running', 'verifying']);

/** "Building · 2m 10s" while the build runs or verifies; just the status label ("Waiting in the build queue") otherwise. */
export function statusPillLabel(status: Build['status'], duration: number | null): string {
  const showDuration = duration !== null && LIVE_DURATION.has(status);
  return `${statusLabel(status)}${showDuration ? ` · ${formatDuration(duration)}` : ''}`;
}

/** The provider account name without its "EAS ·" / "Bitrise ·" prefix (the badge already says which). */
export const providerShortName = (name: string): string => name.replace(/^(EAS|Bitrise)\s*·\s*/i, '');

/** "Open in Bitrise" / "Open in Expo" (platform operators only, SPEC decision 23). */
export const providerLinkName = (kind: string): string => serviceName(kind);

/** The "cancel requested" note shows until the build reaches a terminal status. */
export const showCancelPending = (b: Pick<Build, 'cancelRequestedAt' | 'status'>): boolean => Boolean(b.cancelRequestedAt) && !isTerminal(b.status);

/** Why Retry is offered but disabled: failed builds are the user's code or config. */
export const retryDisabledReason = (b: Pick<Build, 'status'>): string | undefined =>
  b.status === 'failed' ? 'A failed build comes from the app code or its settings: fix it, then start a new build' : undefined;
